"use client";

import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import { FlexRowAlignCenter } from "@/components/Flex";
import StarRating from "@/components/StarRating";
import FollowButton from "@/components/userCardComponents/FollowButton";
import { AuthEvent } from "@/features/Auth/AuthPopup/utils/constants";
import { useCheckAuth } from "@/hooks/useCheckAuth";
import br from "@/styles/breakpoints.module.css";
import { iconSizes } from "@/utils/iconProps";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { UserDetails } from "@knowt/syncing/graphql/schema";
import { useCurrentUserFollowUnfollow } from "@knowt/syncing/hooks/user/useUserFollowingFollowers";
import clsx from "clsx";
import { mutate } from "swr";
import { FollowersMiscProfileBox, FollowingMiscProfileBox } from "./FollowersFollowingMiscBox";
import MiscProfileBox from "./MiscProfileBox";
import LevelsIcon from "@/components/icons/LevelsIcon";

type UserStatsAndSocialMediaProps = {
    user: UserDetails;
    isEditable: boolean;
};

const ProfilePageFollowBtn = ({ userId, enabled }: { userId: string; enabled: boolean }) => {
    const { checkAuth } = useCheckAuth();

    const { isFollowing, toggleFollow } = useCurrentUserFollowUnfollow({ userId: userId, enabled: enabled });

    return (
        <FollowButton
            isFollowing={!!isFollowing}
            onClick={async () => {
                const isAuthenticated = await checkAuth({
                    event: AuthEvent.FOLLOW_USER,
                });

                if (!isAuthenticated) return;

                await mutate(
                    ["publicUserData", userId],
                    data => {
                        if (!data) return data;

                        return {
                            ...data,
                            numFollowers: isFollowing ? data.numFollowers - 1 : data.numFollowers + 1,
                        };
                    },
                    { revalidate: false }
                );

                await toggleFollow();
            }}
        />
    );
};

const UserStats = ({ user, isEditable }: UserStatsAndSocialMediaProps) => {
    return (
        <FlexRowAlignCenter
            className={clsx(br.smDownAlignStart, br.smDownColumnReverse)}
            style={{ gap: "1rem", flexWrap: "wrap" }}>
            <FlexRowAlignCenter style={{ gap: "inherit", flexWrap: "wrap" }}>
                {!isEditable && <ProfilePageFollowBtn userId={user.ID || ""} enabled={!isEditable} />}
                <CircularRectTextButton
                    className="bodyBold2"
                    style={{
                        border: user.profileColor === "#EBE2FF" ? "1px solid #6745AE" : "none",
                        backgroundColor: "#EBE2FF",
                        color: themeColors.pureBlack,
                        height: "5rem",
                        padding: "0.6rem 1.6rem",
                        gap: spacing.XS,
                    }}>
                    <LevelsIcon level={"LV"} size={iconSizes.MD} />
                    {user.level || "0"}
                </CircularRectTextButton>
                <MiscProfileBox
                    style={{
                        padding: "0rem 2rem",
                        backgroundColor: "#FFF7DF",
                        color: themeColors.pureBlack,
                        border: user.profileColor === "#FFF7DF" ? "1px solid #FFC009" : "none",
                        height: "5.2rem",
                    }}
                    leftComponent={
                        <FlexRowAlignCenter className="secondaryTextBold1" style={{ color: "inherit", gap: "0.5rem" }}>
                            <span>{(user?.rating || 0) >= 5 ? "5.0" : user?.rating?.toFixed(1) || "0.0"}</span>
                            <StarRating
                                readOnly
                                max={1}
                                value={user.rating || 0}
                                sx={{ fontSize: "1.8rem", color: "#FFC009" }}
                            />
                            <span className={br.smDownDisplayNone}>({user?.ratingCount || 0})</span>
                        </FlexRowAlignCenter>
                    }
                />
            </FlexRowAlignCenter>
            <FlexRowAlignCenter style={{ gap: "inherit" }}>
                <FollowersMiscProfileBox user={user} />
                <FollowingMiscProfileBox user={user} />
            </FlexRowAlignCenter>
        </FlexRowAlignCenter>
    );
};

export default UserStats;
